import { isRecord } from '../../utils/type-guards';

import {
  assertCdmOfflineConfiguration,
  type CdmOfflineConfiguration,
  getCdmConfigurationQueryKey,
} from './cdm-offline-configuration';
import {
  discoverOfflineLocationScope,
  type OfflineLocationScopeUnavailableReason,
} from './discover-offline-location-scope';
import {
  createOfflineDataStore,
  type OfflineDataStore,
  type OfflineDataStoreIdentity,
} from './offline-data-store';
import { offlineStores } from './use-offline-store/consts';

export type CdmOfflineConfigurationReadResult =
  | { status: 'available'; configuration: CdmOfflineConfiguration }
  | {
      status: 'unavailable';
      reason: OfflineLocationScopeUnavailableReason | 'malformed' | 'invalid';
    };

const isCdmOfflineConfiguration = (
  value: unknown
): value is CdmOfflineConfiguration =>
  isRecord(value) &&
  isRecord(value.user) &&
  [
    value.groups,
    value.locations,
    value.assetViewReferences,
    value.assetViews,
  ].every(Array.isArray);

/** Finds and validates the saved configuration before the selected location is known. */
export const readCdmOfflineConfiguration = async (
  user: OfflineDataStoreIdentity,
  indexedDbFactory: IDBFactory
): Promise<CdmOfflineConfigurationReadResult> => {
  const discovery = await discoverOfflineLocationScope(user, indexedDbFactory);
  if (discovery.status === 'unavailable') return discovery;
  const { scope } = discovery;

  let stored: Awaited<ReturnType<OfflineDataStore['get']>>;
  try {
    stored = await createOfflineDataStore(
      scope,
      offlineStores.Configuration,
      indexedDbFactory
    ).get(getCdmConfigurationQueryKey(scope));
  } catch (error) {
    return {
      status: 'unavailable',
      reason: error instanceof SyntaxError ? 'malformed' : 'unreadable',
    };
  }
  if (stored === undefined) return { status: 'unavailable', reason: 'missing' };

  if (!isCdmOfflineConfiguration(stored)) {
    return { status: 'unavailable', reason: 'invalid' };
  }

  let configuration: CdmOfflineConfiguration;
  try {
    configuration = assertCdmOfflineConfiguration(stored, scope);
  } catch {
    return { status: 'unavailable', reason: 'invalid' };
  }
  return { status: 'available', configuration };
};
